import { isNullOrUndefined } from "../../../../../../../tools";
import { IdProvider } from "../../../../../../services/idprovider.service";
import { ServerCommunicatorService } from "../../../../../../services/server-communicator.service";
import { UIUtils } from "../../../../../../services/uiutils.service";
import { Utils } from "../../../../../../services/utils.service";
import { DataContainer } from "../../../../../infrastructure/datacontainer";
import { DataContainerService } from "../../../../../infrastructure/datacontainer.service";
import { RequestTypes } from "../../../../../infrastructure/enums";
import { IPersistable } from "../../../../../infrastructure/IPersistable";
import { PayloadPacketFacade } from "../../../../../infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../../infrastructure/transportdata";
import { ValidationResultAccumulator } from "../../../../../infrastructure/validationresultaccumulator";
import { ValidationMessages, ValidationPatterns } from "../../../../constants";
import { ZoneFetchRequest } from "./zonefetchrequest";

export class ZoneProps {
  
  public created_date: string = '';
  public updated_date: string = '';
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public name: string = '';
  public state_id: number = 0;
  public readonly state_name_en: string = '';
  public readonly state_name_mr: string = '';

  public readonly is_newly_created: boolean = false;
  

  private constructor(isNewlyCreated: boolean) {
    this.is_newly_created = isNewlyCreated;
  }

  public static Blank() {
    return new ZoneProps(true);
  }
}

export class Zone implements IPersistable<Zone> {
  public static readonly mastertablename: string = 'zone_master';

  private constructor(public readonly p: ZoneProps, public readonly AllowEdit: boolean) {

  }

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0) throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): Zone {
    let newZone: ZoneProps = Utils.GetInstance().DeepCopy(this.p);
    return Zone.CreateInstance(newZone, true);
  }

  public static CreateNewInstance() {
    return new Zone(ZoneProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new Zone(data as ZoneProps, allowEdit);
  }

  public CheckSaveValidity(_td: TransportData, vra: ValidationResultAccumulator): void {
    if (!this.AllowEdit) vra.add('', 'This object is not editable and hence cannot be saved.');
     if (!this.p.name || this.p.name.trim() == '') {
          vra.add('name', 'Name cannot be blank. ');
        }
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(td.MainData, Zone.mastertablename, this.p);
  }

  private static m_currentInstance: Zone = Zone.CreateNewInstance();

  public static GetCurrentInstance() {
    return Zone.m_currentInstance;
  }

  public static SetCurrentInstance(value: Zone) {
    Zone.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): Zone {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, Zone.mastertablename)) {
      for (let data of dcs.GetCollection(td.MainData, Zone.mastertablename)!.Entries) {
        return Zone.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"): Zone[] {
    let result: Zone[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, Zone.mastertablename)) {
      let coll = dcs.GetCollection(cont, Zone.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate)) entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(Zone.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): Zone[] {
    return Zone.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(req: ZoneFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

   // Function created For Trasaction Service filters 
      public static async FetchTransportDataForTransactionFilter(req: ZoneFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
        let tdRequest = req.FormulateTransportData();
        let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);
    
        let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
        if (!tr.Successful) {
          if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
          return null;
        }
    
        let tdResponse = JSON.parse(tr.Tag) as TransportData;
        return tdResponse;
      }

  public static async FetchInstance(id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new ZoneFetchRequest();
    req.zone_ids.push(id);

    let tdResponse = await Zone.FetchTransportData(req, errorHandler) as TransportData;
    return Zone.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(req: ZoneFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdResponse = await Zone.FetchTransportData(req, errorHandler) as TransportData;
    return Zone.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new ZoneFetchRequest();
    let tdResponse = await Zone.FetchTransportData(req, errorHandler) as TransportData;
    return Zone.ListFromTransportData(tdResponse);
  }


  public static async FetchEntireListByStateRef(state_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new ZoneFetchRequest();
    req.state_ids.push(state_id)
    let tdResponse = await Zone.FetchTransportData(req, errorHandler) as TransportData;
    return Zone.ListFromTransportData(tdResponse);
  }

     // get all asha for Trasaction filter service file 
    public static async FetchEntireListForTransactionFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
      let req = new ZoneFetchRequest();
      let tdResponse = await Zone.FetchTransportDataForTransactionFilter(req, errorHandler) as TransportData;
      return Zone.ListFromTransportData(tdResponse);
    }
  

  public async DeleteInstance(successHandler: () => Promise<void> = null!, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    }
    else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }

}
